import {OriginVariables} from "../../../utils/enums";
import {action, computed, makeObservable, observable} from "mobx";

class VariableStore {
  id = null;
  name = "";
  variable = "";
  origin = OriginVariables.FIXED;
  value = "";
  source = null;
  field = null;
  _value = {};

  /**
   * Indicates if this variable is open to show extra information
   * @type {boolean}
   */
  isOpened = false;

  /**
   * Indicates if this variable is default value
   * @type {boolean}
   */
  isDefault = false;

  /**
   * Indicates if this variable is mapped from an external data source
   * @type {boolean}
   */
  isMapped = false;

  /**
   * Indicates if this variable was created by the user rather than system-defined
   * @type {boolean}
   */
  isCustom = false;

  constructor() {
    makeObservable(this, {
      id: observable,
      name: observable,
      variable: observable,
      isDefault: observable,
      isMapped: observable,
      isCustom: observable,
      isOpened: observable,
      origin: observable,
      value: observable,
      source: observable,
      field: observable,
      updateField: action,
      setFields: action,
    });
  }

  setFields(data) {
    this.id = data.id;
    this.name = data.name;
    this.variable = data.variable;
    this.isDefault = data.isDefault;
    this.isMapped = data.isMapped ?? false;
    this.isCustom = data.isCustom ?? false;
    this.origin = data.origin;
    this.value = data.value;
    this.source = data.source;
    this.field = data.field;

    this._value = {
      source: data.source,
      field: data.field
    };
  }

  updateField(field, value) {
    this[field] = value;
    // If user change any value of variable automatically change to custom variable
    this.isCustom = true;
  }
}

export default class TemplateEditorVariablesStore {
  variables = observable.array([]);
  sources = [];
  setTags = null;

  constructor({ setTags }) {
    makeObservable(this, {
      variables: observable,
      sources: observable,
      filteredSources: computed,
      getFilteredFields: action,
      addVariable: action,
      setSources: action,
      deleteVariable: action,
      removeMappedVariables: action,
      updateFieldVariable: action,
      getVariable: action,
      selectVariable: action,
      clean: action,
    });

    this.setTags = setTags;
  }

  selectVariable(id) {
    const findIndex = this.variables.findIndex((variable) => variable.id === id);
    this.variables[findIndex].isOpened = !this.variables[findIndex].isOpened;
  }

  setSources(sources) {
    this.sources = sources;
  }

  removeMappedVariables(){
    const initialCount = this.variables.length;
    this.variables = this.variables.filter((variable) => variable.isMapped === false);
    const removedCount = initialCount - this.variables.length;
    this.onHandleTags();
    return removedCount;
  }

  addVariable({
      name = "",
      variable = "",
      isDefault = false,
      isMapped = false,
      isCustom = false,
      origin = OriginVariables.FIXED,
      value = "",
      source = null,
      field = null
    }) {

    const newVariable = new VariableStore();

    const id = this.variables.length > 0 ? Math.max(...this.variables.map(v => v.id)) + 1 : 1;

    newVariable.setFields({
        id,
        name,
        variable,
        isDefault,
        isMapped,
        isCustom,
        origin,
        value,
        source,
        field,
    });

    this.variables.push(newVariable);
    this.onHandleTags();
}

  getVariable(id) {
    const findIndex = this.variables.findIndex((variable) => variable.id === id);
    return this.variables[findIndex];
  }

  deleteVariable(id) {
    this.variables = this.variables.filter(variable => variable.id !== id);
    this.onHandleTags();
  }

  updateFieldVariable(field, id, value) {
    const findIndex = this.variables.findIndex((variable) => variable.id === id);
    this.variables[findIndex].updateField(field, value);
    this.onHandleTags();
  }

  onHandleTags() {
    const variablesToTags = {};
    for (const variable of this.variables) {
      variablesToTags[variable.variable] = {
        name: variable.name,
        value: `{{${variable.variable}}}`,
      };
    }

    this.setTags(variablesToTags);
  }

  get filteredSources() {
    return this.sources.map((source) => ({name: source.origin, value: source.origin}));
  }

  getFilteredFields(source) {
    if (this.sources && this.sources.length > 0) {
      const selectedOrigin = this.sources.find((field) => field.origin === source);
      if (selectedOrigin) {
        return selectedOrigin.fields?.map((field) => ({
          name: field.name,
          value: field.field,
          field_type: field.field_type,
          operators: field.operators,
          default_values: field.default_values
        }));
      }
    }
    return [];
  }

  clean() {
    this.variables = [];
    this.addVariable({
      name: "",
      variable: "",
      origin: OriginVariables.FIXED,
      value: "",
      isDefault: false,
      isMapped: false,
      isCustom: true,
      source: '',
      field: ''
    });
  }
}